import React, { SyntheticEvent, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Brand } from '../../../../types';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Tab } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { toggleAlert } from '../../../../redux/actions';
import { errorMessageParser } from '../../../../utils/helpers/ToastHelper';
import { AdsTemplate, DiySettings } from '../../../../types/IDiy';
import {
  fetchCampaignTemplates,
  getSingleDiySetting,
} from '../../../../services/diy';
import { DEFAULT_COLOR_THEME } from '../../../../utils/Styling';
import CrmSettingsForm from '../Crm/CrmSettingsForm';
import { CrmSetting } from '../../../../types/ICrm';
import { getCrmSettingsByBrand } from '../../../../services/crm';
import { hubspotOauth2 } from '../../../../services/hubspot';
import { HubSpotOauthData } from '../../../../types/IHubSpot';
import { ADMIN, CrmProvider, SUPER_ADMIN } from '../../../../utils';
import CircularLoading from '../../../CircularLoading';
import { arrayUniqueFilter } from '../../../../utils/arrayFormatter';
import { AuthContext } from '../../../../context';
import FacebookAccountsForm from './FacebookAccountsForm';
import AudienceAsset from './Audience';
import GeolocationSet from './GeolocationSet';
import DemographicsAsset from './DemographicsSet';
import Templates from './Templates';
import { adsProvider } from '../../../../utils/constants/facebookAds';
import TemplateForm from '../../Diy/TemplateForm';
import BusinessAssetz from './Business';
import LeadFormAssetz from './LeadForms';

const FacebookAssetz: React.FC = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');
  const authCode = searchParams.get('code');
  const brand: Brand = useSelector((state: any) => state?.brand?.brand);
  const location: Brand = useSelector(
    (state: any) => state?.location?.location,
  );
  const { state } = useContext(AuthContext);
  const roleBasedId = state.roleBasedId;
  const role = state.role;
  const isSuperAdmin = state.role === SUPER_ADMIN;
  const isAdmin = state.role === ADMIN;
  const [tabValue, setTabValue] = useState<string>('1');
  const [fetchLoading, setFetchLoading] = useState<boolean>(false);
  const [diySettings, setDiySettings] = useState<DiySettings>(null);
  const [crmSettings, setCrmSettings] = useState<CrmSetting>({
    averageLifetimeValue: 0,
    averageCommission: 0,
    averageUnitSold: 1,
    providers: [],
    clientTetherSettings: {
      accessToken: '',
      webKey: '',
      leadSourceId: '',
      enableSyncLeads: true,
    },
  });
  const [hubspotOauthData, setHubspotOauthData] = useState<HubSpotOauthData>({
    accessToken: '',
    refreshToken: '',
    expiresIn: 0,
    currency: '',
    timezone: '',
    accountId: '',
  });
  const [oauthLoading, setOauthLoading] = useState<boolean>(false);
  const [templates, setTemplates] = useState<AdsTemplate[]>([]);
  const [openTemplateForm, setOpenTemplateForm] = useState<boolean>(false);
  const [templateAction, setTemplateAction] = useState<
    'create' | 'edit' | 'duplicate'
  >('create');
  const [selectedTemplate, setSelectedTemplate] = useState<AdsTemplate>(null);
  const [tabs, setTabs] = useState<string[]>([]);

  useEffect(() => {
    const builtTabs = buildTabs();

    setTabs(builtTabs);
  }, [diySettings?.fbAdAccount, diySettings?.facebookPage]);

  useEffect(() => {
    if (type === 'crm') {
      const index = tabs.findIndex((tab: string) => tab === 'CRM');

      setTabValue((index + 1).toString());
    }

    if (type === 'forms') {
      const index = tabs.findIndex((tab: string) => tab === 'Lead Forms');

      setTabValue((index + 1).toString());
    }
  }, [type, tabs]);

  useEffect(() => {
    if (authCode && (location || brand)) {
      hubspotAuthentication();
    }
  }, [authCode, location, brand]);

  useEffect(() => {
    if (location || brand) {
      getDiySettings();
      getTemplates();
    }
  }, [brand, location]);

  useEffect(() => {
    if (location || brand) {
      getBrandCrmSettings();
    }
  }, [location, brand, authCode, hubspotOauthData]);

  const getTemplates = async () => {
    try {
      setFetchLoading(true);

      const response = await fetchCampaignTemplates(
        ((location || brand)?.__type === 'location'
          ? (location || brand)?.franchisor
          : location || brand
        )?._id,
        adsProvider.FACEBOOK,
        true,
      );

      setTemplates(response.data);
    } catch (error: any) {
      console.log(error.message);
    } finally {
      setFetchLoading(false);
    }
  };

  const getDiySettings = async () => {
    try {
      setFetchLoading(true);
      const response = await getSingleDiySetting((location || brand)?._id);

      setDiySettings(response.data);
    } catch (error: any) {
      console.log(error);
    } finally {
      setFetchLoading(false);
    }
  };

  const getBrandCrmSettings = async () => {
    try {
      setFetchLoading(true);
      const response = await getCrmSettingsByBrand((location || brand)?._id);

      let temp: CrmSetting = { ...response.data };

      if (authCode && hubspotOauthData?.accessToken) {
        const providers = [...(temp?.providers || []), CrmProvider.HUBSPOT];
        temp = {
          ...temp,
          hubspotSettings: {
            ...temp.hubspotSettings,
            accessToken: hubspotOauthData.accessToken,
            refreshToken: hubspotOauthData.refreshToken,
            expiresIn: hubspotOauthData.expiresIn,
          },
          providers: arrayUniqueFilter(providers),
        };
      }

      setCrmSettings(temp);
    } catch (error: any) {
      console.log(error);
    } finally {
      setFetchLoading(false);
    }
  };

  const hubspotAuthentication = async () => {
    try {
      setOauthLoading(true);
      const response = await hubspotOauth2((location || brand)?._id, authCode);

      setHubspotOauthData(response.data.hubspotSettings);
    } catch (error: any) {
      const errorMsg = errorMessageParser(error);

      dispatch(
        toggleAlert({
          toggle: true,
          message: errorMsg,
          type: 'error',
        }),
      );
    } finally {
      setOauthLoading(false);
    }
  };

  const buildTabs = () => {
    let tabs: string[] = ['Accounts', 'Business'];

    if (
      diySettings?.fbAdAccount?.accountId &&
      diySettings?.facebookPage?.fbPageId
    ) {
      tabs = [
        ...tabs,
        'Templates',
        // TODO: Enable if necessary
        // 'Images',
        // 'Videos',
        'Location Sets',
        'Audiences',
        'Demographics',
        'Lead Forms',
      ];
    }

    tabs = [...tabs, 'CRM'];

    return tabs;
  };

  const handleChangeTabValue = (event: SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const handleCloseForm = () => {
    setOpenTemplateForm(false);
  };

  const renderTabContent = (tab: string) => {
    if (tab === 'Accounts') {
      return (
        <FacebookAccountsForm
          brand={brand}
          location={location}
          diySettings={diySettings}
          setDiySettings={setDiySettings}
          currentUserId={roleBasedId}
          franchisor={(location || brand)?.franchisor}
          role={role}
          isAdmin={isSuperAdmin || isAdmin}
        />
      );
    }

    if (tab === 'Business') {
      return (
        <BusinessAssetz
          brand={location || brand}
          diySettings={diySettings}
          setDiySettings={setDiySettings}
        />
      );
    }

    /**
     * TODO: Enable if necessary
    if (tab === 'Images') {
      return <FacebookAccountImages brand={location || brand} />;
    }
    */

    if (tab === 'Location Sets') {
      return <GeolocationSet brand={location || brand} />;
    }

    if (tab === 'Lead Forms') {
      return <LeadFormAssetz brand={location || brand} />;
    }

    if (tab === 'CRM') {
      return (
        <CrmSettingsForm
          brand={location || brand}
          crmSettings={crmSettings}
          setCrmSettings={setCrmSettings}
        />
      );
    }

    if (tab === 'Audiences') {
      return (
        <AudienceAsset diySettings={diySettings} brand={location || brand} />
      );
    }

    if (tab === 'Demographics') {
      return <DemographicsAsset brand={location || brand} />;
    }

    if (tab === 'Templates') {
      return (
        <Templates
          brand={location || brand}
          templates={templates}
          setTemplates={setTemplates}
          loading={fetchLoading}
          setOpenForm={setOpenTemplateForm}
          setFormAction={setTemplateAction}
          setSelectedTemplate={setSelectedTemplate}
          selectedTemplate={selectedTemplate}
        />
      );
    }
  };

  return openTemplateForm ? (
    <TemplateForm
      brand={location || brand}
      isEdit={templateAction === 'edit'}
      isDuplicate={templateAction === 'duplicate'}
      templates={templates}
      setTemplates={setTemplates}
      onClose={handleCloseForm}
      templateId={selectedTemplate?._id}
    />
  ) : (
    <div>
      <CircularLoading loading={oauthLoading} />

      <TabContext value={tabValue}>
        <TabList
          onChange={handleChangeTabValue}
          variant="scrollable"
          allowScrollButtonsMobile
          scrollButtons
          textColor="secondary"
          TabIndicatorProps={{ style: { backgroundColor: 'white' } }}
          TabScrollButtonProps={{
            style: { backgroundColor: DEFAULT_COLOR_THEME, color: 'white' },
          }}
          sx={{
            backgroundColor: DEFAULT_COLOR_THEME,
            '.MuiTab-root': {
              top: 5,
              color: 'white',
            },
            '.Mui-selected': {
              color: `${DEFAULT_COLOR_THEME} !important`,
              backgroundColor: 'white',
              top: 5,
              borderRadius: 2,
            },
          }}
        >
          {tabs.map((tab: string, index: number) => (
            <Tab
              label={tab}
              value={`${index + 1}`}
              key={`assetz-tab-header-${index + 1}`}
            />
          ))}
        </TabList>

        {tabs.map((tab: string, index: number) => {
          return (
            <TabPanel
              value={`${index + 1}`}
              sx={{
                backgroundColor: '#d9d9d933',
                minHeight: `calc(100vh - 255px)`,
                maxHeight: `calc(100vh - 255px)`,
                overflowY: 'auto',
                overflowX: 'hidden',
                '&::-webkit-scrollbar': {
                  width: '10px',
                },
                '&::-webkit-scrollbar-track': {
                  borderTopRightRadius: '5px',
                  borderBottomRightRadius: '5px',
                },

                '&::-webkit-scrollbar-thumb': {
                  background: DEFAULT_COLOR_THEME,
                  borderRadius: '18px',

                  '&:hover': {
                    background: DEFAULT_COLOR_THEME,
                  },
                },
              }}
              key={`assetz-content-${index + 1}`}
            >
              {renderTabContent(tab)}
            </TabPanel>
          );
        })}
      </TabContext>
    </div>
  );
};

export default FacebookAssetz;
